import React, { FC, memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@blocs.i18n';
import { gqlProfileContactInfo, gqlWfProfileContactInfo } from '@minecraft.graphql-types';
import Icon from '@minecraft.icon';

const Container = styled.div`
  font-size: 0.875rem;
  width: 100%;
`;

const StyledIcon = styled(Icon)`
  width: 1rem;
  height: 1rem;
  margin-right: 0.25rem;
`;

const LinkWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-weight: normal;
  white-space: break-spaces;

  span {
    flex: auto;
  }
`;

const Link = styled.a`
  font-size: inherit;
  font-weight: inherit;
  text-decoration: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &:not(:first-child) {
    padding-top: 0.25rem;
  }
`;

const PhoneLink = styled(Link)`
  &,
  &:focus,
  &:visited,
  &:active,
  &:hover {
    color: ${(props): string => props.theme.colors.primary.darkGrey};
  }
`;

type Props = {
  withIcons?: boolean;
  withEmail?: boolean;
  isRepresented?: boolean;
  contactInfo: gqlProfileContactInfo | gqlWfProfileContactInfo;
  formatPhone?: (phone: string) => string;
  className?: string;
  qaId?: string;
};

const CDProfileContactInfo: FC<Props> = ({
  withIcons = true,
  withEmail = true,
  isRepresented,
  contactInfo,
  formatPhone,
  className,
  qaId,
}) => {
  const { t } = useTranslation();

  const whose = isRepresented ? t('common:label.rep') : t('common:label.talent');

  return (
    <Container className={className} data-testid="contact-info-container">
      {contactInfo.phone && (
        <LinkWrapper data-testid="phone-wrapper">
          <PhoneLink href={`tel:${contactInfo.phone}`} data-qa-id={`${qaId}-phone`}>
            {withIcons && <StyledIcon name="talent-phone-number" inheritColor />}
            {formatPhone?.(contactInfo.phone) || contactInfo.phone}
          </PhoneLink>
          <span>{` (${whose})`}</span>
        </LinkWrapper>
      )}
      {withEmail && contactInfo.email && (
        <LinkWrapper data-testid="email-wrapper">
          <Link href={`mailto:${contactInfo.email}`} data-qa-id={`${qaId}-email`}>
            {withIcons && <StyledIcon name="email" inheritColor />}
            {contactInfo.email}
          </Link>
          <span>{` (${whose})`}</span>
        </LinkWrapper>
      )}
    </Container>
  );
};

export default memo(CDProfileContactInfo);
